import OptionSelectionDropDown from "components/dropdown/OptionSelectionDropDown";
import styles from "components/input/optionSelector.module.scss";
import classNames from "classnames";
import utilStyles from "styles/utils.module.scss";
import {formattedPrice, numberWithComma} from "common/utils";
import CloseIcon from "images/icons/close.svg";
import {useEffect, useMemo, useState} from "react";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import NewQuantitySelector from "components/buttons/NewQuantitySelector";
import useTranslation from "next-translate/useTranslation";


const OptionText = (props) => {
    const { t } = useTranslation('dropdown-OptionSelectionDropdownWithQuantitySelector');
    return <span className={styles.optionText}>{t('OptionSelectionDropdownWithQuantitySelector.optionPrefix')}{props.name}</span>
}


export default function OptionSelectionDropdownWithQuantitySelector(props) {
    const {optionId, selectedOptions, setSelectedOptions, placeholder, disallowAdditionalPrice, product, fixedCount, allowSoldOut, hidePrice, ignoreMaxCount, isExtraOptionNotAllowed} = props;
    const unitCount = fixedCount || 1;
    const [isExpanded, setIsExpanded] = useState(false);
    const {t} = useTranslation('dropdown-OptionSelectionDropdownWithQuantitySelector');

    const optionIdToOptions = Object.assign({}, ...props.options?.map(x => ({[x.id]: x})) || []);

    let basePrice;

    const options = useMemo(() => {
        let _options = [];
        if (optionId in optionIdToOptions) {
            const originalOption = optionIdToOptions[optionId];
            const optionPrice = originalOption.discounted_price;
            basePrice = originalOption.discounted_price;

            _options = props.options ? props.options.filter(x => !disallowAdditionalPrice || optionId === x.id || x.discounted_price === optionPrice) : [];
        }
        return _options;
    }, [props.options]);

    const maxCount = props.maxCount ? props.maxCount : -1;

    let isSoldOut = true;
    for (const option of options) {
        if (!option.is_sold_out) {
            isSoldOut = false;
        }
    }
    if (options.length === 0) {
        isSoldOut = false;
    }

    let totalCount = 0;
    for (const [optionId, count] of Object.entries(selectedOptions)) {
        totalCount += count;
    }

    const addOptionCount = (optionId, amount) => {
        if (amount !== unitCount) {
            alert(t('OptionSelectionDropdownWithQuantitySelector.quantityChangeNotAllowed'))
            return;
        }
        if (amount > 0 && !ignoreMaxCount && totalCount >= maxCount) {
            alert(t('OptionSelectionDropdownWithQuantitySelector.maxQuantityReached'));
            return;
        }
        const maxAmount = maxCount - totalCount;
        const addAmount = Math.min(amount, maxAmount);
        setSelectedOptions(val => {
            const newVal = val[optionId] ? val[optionId] + addAmount : addAmount;
            const newDict = JSON.parse(JSON.stringify(val));
            newDict[optionId] = newVal;
            if (newVal === 0) {
                delete newDict[optionId];
            }
            return newDict;
        });
    };

    const setOptionCount = (optionId, amount) => {
        if (amount > 0 && !ignoreMaxCount && totalCount >= maxCount) {
            alert(t('OptionSelectionDropdownWithQuantitySelector.maxQuantityReached'));
            return;
        }
        const finalAmount = Math.min(maxCount, amount);
        setSelectedOptions(val => {
            const newVal = finalAmount;
            const newDict = JSON.parse(JSON.stringify(val));
            newDict[optionId] = newVal;
            if (newVal === 0) {
                delete newDict[optionId];
            }
            return newDict;
        });
    };

    useEffect(() => {
        if (options.length === 1) {
            const option = options[0];
            if (!option.is_sold_out && totalCount < maxCount) {
                if (ignoreMaxCount) {
                    addOptionCount(option.id, 1);
                } else {
                    if (totalCount < maxCount) {
                        addOptionCount(option.id, maxCount - totalCount);
                    }
                }

            }
        }
    }, []);

    const deleteOptionCount = (optionId) => {
        setSelectedOptions(val => {
            const newDict = JSON.parse(JSON.stringify(val));
            if (newDict.hasOwnProperty(optionId)) {
                delete newDict[optionId];
            }
            return newDict;
        })
    }

    return (
        <div>
            {
                options.length === 0
                    ? <span className={styles.noOptionText}>{t('OptionSelectionDropdownWithQuantitySelector.noAvailableProducts')}</span>
                    :
                    <>
                        <OptionSelectionDropDown
                            allowSoldOut={allowSoldOut}
                            isExpanded={isExpanded} setIsExpanded={setIsExpanded}
                            basePrice={basePrice} options={options}
                            title={placeholder}
                            small
                            onClick={(e) => {
                                if ((selectedOptions[e.id] || 0) > 0) {
                                    alert(t('OptionSelectionDropdownWithQuantitySelector.alreadySelectedOption'));
                                }
                                else if (!ignoreMaxCount && totalCount >= maxCount) {
                                    alert(t('OptionSelectionDropdownWithQuantitySelector.maxQuantityReached'));
                                }
                                else {
                                    addOptionCount(e.id, unitCount);
                                }
                            }}
                            displayOnSingle
                            product={product}
                            hidePrice={hidePrice}
                            selectedOptions={selectedOptions}
                            isExtraOptionNotAllowed={isExtraOptionNotAllowed}
                        />
                        {
                            <div {...{'body-scroll-lock-ignore': "true"}} className={styles.resultContainer}
                                 style={{marginTop: Object.keys(selectedOptions).length > 0 ? 12 : 0, overflow: 'auto',}}>
                                {
                                    Object.keys(selectedOptions).map((key, i) => {
                                        const count = selectedOptions[key];
                                        const option = optionIdToOptions[key];
                                        return (
                                            <div className={styles.result} key={i}>
                                                {
                                                    option &&
                                                    <>
                                                        <OptionText name={option.name}/>
                                                        <div className={styles.selectorContainer} style={{
                                                            alignItems: 'center', marginTop: 12
                                                        }}>
                                                            <div className={classNames(utilStyles.flexRow, utilStyles.alignItemsCenter)}>
                                                                <NewQuantitySelector
                                                                    count={count}
                                                                    fontSize={14}
                                                                    onPlus={() => addOptionCount(option.id, 1)}
                                                                    onMinus={() => addOptionCount(option.id, -1)}
                                                                    setCount={(amount) => setOptionCount(option.id, amount)}
                                                                    max={maxCount}
                                                                />
                                                                <div style={{width: 12}}/>
                                                            </div>
                                                            <div>
                                                                {
                                                                    isSoldOut && !allowSoldOut ?
                                                                        <span className={styles.soldOutText}>{t('OptionSelectionDropdownWithQuantitySelector.soldOut')}</span>
                                                                        :
                                                                        <>
                                                                        {
                                                                            !hidePrice &&
                                                                            <>
                                                                                <span className={styles.price}>
                                                                                    {formattedPrice(option.discounted_price * count, option?.currency)}
                                                                                </span>
                                                                                <span className={styles.won}>{t(`common:currency.${option?.currency}`)}</span>
                                                                            </>
                                                                        }
                                                                            {
                                                                                options.length !== 1 &&
                                                                                <CloseIcon className={styles.close} viewBox="0 0 20 20"
                                                                                           onClick={() => deleteOptionCount(option.id)}/>
                                                                            }
                                                                        </>
                                                                }
                                                            </div>
                                                        </div>
                                                    </>
                                                }
                                            </div>
                                        );
                                    })
                                }
                            </div>
                        }

                    </>
            }


        </div>
    )
}
